import React, { useState, useEffect, useMemo } from 'react';
import { getActivityLog } from '../services/apiService';
import { ActivityLog } from '../types';
import { LoadingSpinner } from './icons';

const LogCard: React.FC<{ log: ActivityLog }> = ({ log }) => (
    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex justify-between items-start mb-2">
            <p className="font-bold text-gray-900">{log.userName}</p>
             <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                {log.action}
            </span>
        </div>
        <p className="text-sm text-gray-700 mb-2">{log.details}</p>
        <p className="text-right text-xs text-gray-500">{new Date(log.timestamp).toLocaleString()}</p>
    </div>
);


const ActivityLogPage: React.FC = () => {
    const [logs, setLogs] = useState<ActivityLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [userFilter, setUserFilter] = useState('');
    const [actionFilter, setActionFilter] = useState('All');

    useEffect(() => {
        const fetchLogs = async () => {
            setLoading(true);
            try {
                const data = await getActivityLog();
                setLogs(data);
            } catch (err) {
                setError("Failed to fetch activity logs.");
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, []);

    const uniqueActions = useMemo(() => {
        const actions = new Set(logs.map(log => log.action));
        return ['All', ...Array.from(actions)];
    }, [logs]);

    const filteredLogs = useMemo(() => {
        return logs.filter(log => {
            const userMatch = log.userName.toLowerCase().includes(userFilter.toLowerCase());
            const actionMatch = actionFilter === 'All' || log.action === actionFilter;
            return userMatch && actionMatch;
        });
    }, [logs, userFilter, actionFilter]);

    return (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">System Activity Log</h1>

            <div className="mb-6 flex flex-col md:flex-row items-center gap-4">
                <input
                    type="text"
                    placeholder="Filter by user name..."
                    value={userFilter}
                    onChange={e => setUserFilter(e.target.value)}
                    className="w-full md:w-1/2 p-2 border border-gray-300 rounded-md focus:ring-primary-light focus:border-primary-light"
                />
                <select
                    value={actionFilter}
                    onChange={e => setActionFilter(e.target.value)}
                    className="w-full md:w-1/2 p-2 border border-gray-300 rounded-md focus:ring-primary-light focus:border-primary-light"
                >
                    {uniqueActions.map(action => (
                        <option key={action} value={action}>{action}</option>
                    ))}
                </select>
            </div>

            {loading && <div className="flex justify-center items-center py-8"><LoadingSpinner className="w-8 h-8 text-primary" /></div>}
            {error && <div className="text-red-500 p-4 bg-red-100 rounded-md">{error}</div>}

            {!loading && !error && (
                <>
                    {/* Desktop Table */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredLogs.length > 0 ? filteredLogs.map(log => (
                                    <tr key={log.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(log.timestamp).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{log.userName}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                                {log.action}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700">{log.details}</td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={4} className="text-center text-gray-500 py-8">No log entries found for the selected filters.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                     {/* Mobile Cards */}
                    <div className="md:hidden space-y-3">
                        {filteredLogs.length > 0 ? filteredLogs.map(log => (
                            <LogCard key={log.id} log={log} />
                        )) : (
                             <p className="text-center text-gray-500 py-8">No log entries found for the selected filters.</p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ActivityLogPage;